import { useState } from 'react';
import { formatCaptureDraftAge, type CaptureDraft } from '../../data/drafts/capture-draft';
import type { CaptureAudioMatch } from '../../data/drafts/capture-audio';
import './captures.css';

/**
 * THE RECOVERY CARD — M4 / S-04-01, D-019 (J-3).
 *
 * Jerry's decision was explicit: on relaunch with an unfinished Capture,
 * Crewlink does NOT navigate Brandon back into it. It shows a card, says what
 * the unfinished Capture is, and lets him decide. Auto-opening would take a man
 * standing in front of a different problem and drop him into yesterday's — and
 * it would do it before he could see what he was being dropped into.
 *
 * WHAT THE CARD MUST SAY, because a prompt he cannot act on is worse than
 * silence: that an unfinished Capture exists, WHICH PROJECT it belongs to, and
 * roughly how old it is. Nothing here is inferred from a network read, so the
 * card is fully truthful in a basement with no signal.
 *
 * THREE HONEST EXTRAS, each one a thing Brandon would otherwise discover the
 * hard way:
 *
 *   * A draft whose Save was dispatched but never confirmed says **"Waiting to
 *     send"** — the locked D-002 wording. Never "Saved", which would claim a
 *     server truth nobody has; never "Failed", which would claim the opposite.
 *   * A draft that was recording when the process died says so, and says the
 *     recording itself could not be kept. Jerry decided against partial-audio
 *     recovery (D-019 J-1), so the honest thing is to admit the loss rather
 *     than let him wonder where his words went.
 *   * A short preview of the words, so Discard is a decision rather than a
 *     gamble.
 *
 * DISCARD IS TWO TAPS. It permanently destroys the only copy of work Brandon
 * cannot get back, on a phone he is holding in a work glove. A confirm step is
 * the difference between a mis-tap costing a second and costing a Capture. It
 * is inline rather than a modal, so nothing blocks the screen (J-3).
 */
export function CaptureRecoveryCard({
  draft,
  nowMs,
  audio = 'none',
  onContinue,
  onDiscard,
}: {
  draft: CaptureDraft;
  /**
   * Whether a temporary recording is waiting with this draft (S-04-02).
   *
   * The card SAYS SO and stops there: Transcribe is not offered here. Deciding
   * to transcribe belongs on the Capture screen, next to the note the words
   * will land in — putting it on the launch surface would let Brandon start a
   * transcription of a Capture he cannot see.
   */
  audio?: CaptureAudioMatch;
  /**
   * The instant the draft was READ, supplied by the caller.
   *
   * Not `Date.now()` in here: the clock is not readable during render, and an
   * age fixed at read time is the honest one anyway — it does not creep upward
   * while Brandon is deciding. It also makes the line deterministic under test.
   */
  nowMs: number;
  onContinue: () => void;
  onDiscard: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const pending = draft.lifecycle === 'pending-save';
  const preview = draft.note.trim();
  const hasRecording = audio === 'ready' || audio === 'expired';

  return (
    <section className="capture-recovery" aria-label="Unfinished Capture">
      <p className="capture-recovery-label">UNFINISHED CAPTURE</p>
      <h2 className="capture-recovery-project">{draft.projectName}</h2>
      <p className="capture-recovery-meta">{formatCaptureDraftAge(draft.updatedAtMs, nowMs)}</p>

      {pending ? (
        <p className="capture-recovery-pending" role="status">
          Waiting to send. This Capture was sent, but Crewlink closed before the server confirmed
          it. Sending it again cannot create a second Capture.
        </p>
      ) : null}

      {hasRecording ? (
        <p className="capture-recovery-audio" role="status">
          {audio === 'expired'
            ? 'A recording is still waiting to be transcribed, and it has been waiting longer than Crewlink keeps one. Continue to transcribe it or discard it.'
            : 'A recording is ready to transcribe.'}
        </p>
      ) : null}

      {/* TWO DIFFERENT TRUTHS, AND THE CARD MUST TELL THE SAME ONE THE CAPTURE
          SCREEN DOES (M4 / S-04-03, I-038).

          `recordingInProgress` — a recording was RUNNING when Crewlink died.
          Nothing was ever safely finished, so nothing could be kept (D-019
          J-1). Unchanged.

          `audio === 'lost'` — the draft points at a FINISHED recording that is
          not there. That is usually damage, but it is also exactly what a
          deliberate Discard looks like when its pointer-clear silently failed,
          and "could not be kept" would be a small lie in that case. The
          sentence is generic and true either way. */}
      {audio === 'lost' || draft.recordingInProgress ? (
        <p className="capture-recovery-lost">
          {draft.recordingInProgress
            ? 'A recording was still unfinished when Crewlink closed, and the recording itself could not be kept. Anything typed or already transcribed is below.'
            : 'That recording is no longer available. Anything typed or already transcribed is below.'}
        </p>
      ) : null}

      {preview.length > 0 ? (
        <p className="capture-recovery-preview">{preview}</p>
      ) : (
        <p className="capture-recovery-preview capture-recovery-preview--empty">
          No text was written yet.
        </p>
      )}

      {confirming ? (
        <>
          <p className="capture-recovery-confirm">
            {hasRecording
              ? 'Discard this unfinished Capture? The recording and the text above are deleted from this device and cannot be brought back.'
              : 'Discard this unfinished Capture? The text above is deleted from this device and cannot be brought back.'}
          </p>
          <div className="capture-recovery-actions">
            <button type="button" className="capture-recovery-discard" onClick={onDiscard}>
              Yes, discard it
            </button>
            <button
              type="button"
              className="capture-recovery-keep"
              onClick={() => {
                setConfirming(false);
              }}
            >
              Keep it
            </button>
          </div>
        </>
      ) : (
        <div className="capture-recovery-actions">
          <button type="button" className="capture-recovery-continue" onClick={onContinue}>
            {pending ? 'Continue and send' : 'Continue Capture'}
          </button>
          <button
            type="button"
            className="capture-recovery-discard"
            onClick={() => {
              setConfirming(true);
            }}
          >
            Discard
          </button>
        </div>
      )}
    </section>
  );
}
